import {
	FunctionComponent,
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { SettingsPage } from "../SettingsPage";

import { IFormHandler, useFormHandler } from "client/Forms";
import {
	ConnectionModel,
	createGateway,
	deleteConnections,
	GatewayModel,
	GatewaySaveModel,
	GatewayVersion,
	GatewayViewModel,
	getCreateGatewayViewModel,
	getEditGatewayViewModel,
	getViewGatewayViewModel,
	updateGateway
} from "client/Api/ClientlessRdp/clientlessRdp";
import {
	Checkbox,
	ConfirmationDialog,
	Form,
	FormItem,
	SecretValueInput,
	Select,
	TextInput,
	ToolbarAction,
	ToolbarActionGroup,
	ValidationError
} from "client/Components";
import {
	createValidationConfiguration,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined,
	isUrl
} from "client/Validation/rules";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { ConnectionsSection } from "./ConnectionsSection";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import {
	ensureNotNil,
	isNotNil
} from "client/Misc/utils";
import { tryParseInt } from "client/Misc/parseUtils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { clientlessRdpRouting } from "./GatewayOverview";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	RouteParametersFromProps,
	connectToRouter
} from "client/Navigation";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

const {
	Section
} = SettingsPage;

const validationConfiguration: IValidationConfiguration<GatewaySaveModel> = createValidationConfiguration<GatewaySaveModel>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validationConfiguration.forScalar("gatewayUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: true
		})
	);

validationConfiguration.forScalar("serviceAccountUsername")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validationConfiguration.forObject("serviceAccountPassword")
	.when((x) => x.containsNewValue)
	.forScalar("value")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validationConfiguration.forScalar("versionId")
	.add(
		isNotNullNorUndefined()
	);

export type GatewayConfigurationProps = {
	isReadOnly: boolean;
	gatewayId?: number;
	onNavigate: NavigationCallback;
};

const GatewayConfiguration = (props: GatewayConfigurationProps): ReactElement => {
	const {
		isReadOnly,
		gatewayId,
		onNavigate
	} = props;

	const [viewModel, setViewModel] = useState<GatewayViewModel>(defaultViewModel);

	const {
		model,
		connections,
		failedToLoadConnections,
		versions
	} = viewModel;

	const [selectedConnections, setSelectedConnections] = useState<readonly ConnectionModel[]>([]);

	const [isMakingRequest, setIsMakingRequest] = useState<boolean>(false);
	const [connectionIdsToBeDeleted, setConnectionIdsToBeDeleted] = useState<readonly string[] | undefined>();
	const [saveModel, setSaveModel] = useState<GatewaySaveModel>(() => convertToSaveModel(model, versions));
	const [lastPersistedModel, setLastPersistedModel] = useState<GatewaySaveModel>(() => convertToSaveModel(model, versions));

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const alertManager: AlertManager = useAlerts();
	useActiveSettingsMenuItem(SettingsPageKind.ClientlessRDP);
	const formHandler: IFormHandler<GatewaySaveModel> = useFormHandler<GatewaySaveModel>(
		saveModel,
		setSaveModel,
		validationConfiguration
	);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsMakingRequest(true);
					let viewModel: GatewayViewModel;
					if (isReadOnly) {
						viewModel = await getViewGatewayViewModel(ensureNotNil(gatewayId), signal)
							.catch(handlePageDataLoadingError);
					} else if (isNotNil(gatewayId)) {
						viewModel = await getEditGatewayViewModel(gatewayId, signal)
							.catch(handlePageDataLoadingError);
					} else {
						viewModel = await getCreateGatewayViewModel(signal)
							.catch(handlePageDataLoadingError);
					}

					setViewModel(viewModel);

					const saveModel: GatewaySaveModel = convertToSaveModel(
						viewModel.model,
						viewModel.versions
					);

					setSaveModel(saveModel);
					setLastPersistedModel(saveModel);

					setIsMakingRequest(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsMakingRequest(false);
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			gatewayId,
			isReadOnly,
			alertManager
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isMakingRequest) {
				return;
			}
			setIsMakingRequest(true);

			const validationResult: InputValidationResult = await formHandler.validation
				.validate();

			if (validationResult.kind !== InputValidationResultKind.Valid) {
				alertManager.showError(localize("_NotAllRequiredFieldsAreFilledIn"));
				setIsMakingRequest(false);
				return;
			}

			try {
				let resultingGatewayId: number;
				if (!gatewayId) {
					resultingGatewayId = await createGateway(saveModel);
				} else {
					await updateGateway(gatewayId, saveModel);
					resultingGatewayId = gatewayId;
				}

				setLastPersistedModel(saveModel);
				onNavigate(clientlessRdpRouting.gatewayDetails({gatewayId: `${resultingGatewayId}`}));
			} catch (e) {
				if (!formHandler.validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_UnknownError"));
				}

				setIsMakingRequest(false);
			}
		},
		[
			isMakingRequest,
			formHandler.validation,
			alertManager,
			gatewayId,
			saveModel,
			onNavigate
		]
	);

	const cancelHandler = useCallback(
		(gatewayId: number | undefined): void => {
			setSaveModel(lastPersistedModel);

			formHandler.validation
				.reset();

			onNavigate(
				isNotNil(gatewayId)
					? clientlessRdpRouting.gatewayDetails({gatewayId: `${gatewayId}`})
					: clientlessRdpRouting.gatewayOverview
			);
		},
		[
			formHandler.validation,
			lastPersistedModel,
			onNavigate
		]
	);

	const navigateToOverview = useCallback(
		(): void => {
			onNavigate(clientlessRdpRouting.gatewayOverview);
		},
		[onNavigate]
	);

	const actionHandlers = useMemo(
		(): ActionHandlers => {
			return {
				deselectHandler: () => setSelectedConnections([]),
				createConnectionHandler: (gatewayId) => onNavigate(clientlessRdpRouting.createConnection({gatewayId: `${gatewayId}`})),
				editConnectionHandler: (gatewayId, connectionId) => onNavigate(
					clientlessRdpRouting.editConnection({
						gatewayId: `${gatewayId}`,
						connectionId
					})
				),
				navigateToEdit: (gatewayId) => onNavigate(clientlessRdpRouting.editGateway({gatewayId: `${gatewayId}`})),
				cancelHandler,
				viewConnectionHandler: (gatewayId, connectionId) => onNavigate(
					clientlessRdpRouting.connectionDetails({
						gatewayId: `${gatewayId}`,
						connectionId
					})
				),
				saveHandler,
				deleteConnectionsHandler: (ids) => setConnectionIdsToBeDeleted(ids)
			};
		},
		[
			onNavigate,
			cancelHandler,
			saveHandler
		]
	);

	const selectionActions = useMemo(
		(): readonly GatewayAction[] => {
			return getSelectionActions(actionHandlers);
		},
		[actionHandlers]
	);

	const contextualActions = useMemo(
		(): readonly GatewayAction[] => {
			return listContextualActions(actionHandlers);
		},
		[actionHandlers]
	);

	const defaultActions = useMemo(
		(): readonly GatewayAction[] => {
			return listDefaultActions(actionHandlers);
		},
		[actionHandlers]
	);

	const actionContext = useMemo(
		(): GatewayActionContext => {
			return {
				gatewayId,
				isEditing: !isReadOnly,
				selectedConnectionIds: selectedConnections.map(x => x.id!),
				isMobile
			};
		},
		[
			gatewayId,
			isReadOnly,
			selectedConnections,
			isMobile
		]
	);

	const hasSelection: boolean = actionContext.selectedConnectionIds
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly GatewayAction[] => {
			return buildActionsForTopBar<GatewayActionContext, GatewayAction>(
				{
					selectionActions,
					contextualActions,
					defaultActions,
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			selectionActions,
			contextualActions,
			defaultActions,
			hasSelection,
			isResponsive
		]
	);

	const deleteConnectionsHandler = async (ids: readonly string[]): Promise<void> => {
		if (!gatewayId) {
			throw new Error("Cannot delete connections from a new gateway");
		}
		setIsMakingRequest(true);
		setConnectionIdsToBeDeleted(undefined);
		try {
			await deleteConnections(gatewayId, ids);
			setViewModel(
				(x) => ({
					...x,
					connections: x.connections
						.filter(x => ids.indexOf(x.id!) === -1)
				})
			);

			alertManager.showSuccess(localize("_ConnectionDeletedSuccessfully"));
		} catch (e) {
			console.error(e);
			alertManager.showError(localize("_ErrorDuringConnectionDeletion"));
		} finally {
			setIsMakingRequest(false);
		}
	};

	const urlAndUsernameChangeHandler = (): void => {
		const passwordReducer = formHandler.reducer.for("serviceAccountPassword");
		if (!passwordReducer.value.containsNewValue) {
			passwordReducer.setValue({
				containsNewValue: true,
				value: ""
			});
		}
	};

	return (
		<SettingsPage
			hasBusyIndicator={isMakingRequest}
			actions={topBarActions}
			actionContext={actionContext}
			hasSelection={hasSelection}
			title={localize("_ConfigureGateway")}
			onBack={navigateToOverview}
		>
			<Section>
				<Form
					onSubmit="NONE"
					hasWidthConstrained
				>
					<FormItem label={localize("_Name")}>
						<TextInput
							{...formHandler.for("name").attachToTextInput()}
							isDisabled={isReadOnly}
						/>
						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_GatewayUrl")}>
						<TextInput
							{...formHandler
								.for("gatewayUrl")
								.attachToTextInput()
								.withOnChange(urlAndUsernameChangeHandler)
							}
							isDisabled={isReadOnly}
						/>
						<ValidationError {...formHandler.for("gatewayUrl").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_ServiceAccountUserName")}>
						<TextInput
							{...formHandler
								.for("serviceAccountUsername")
								.attachToTextInput()
								.withOnChange(urlAndUsernameChangeHandler)
							}
							isDisabled={isReadOnly}
						/>
						<ValidationError {...formHandler.for("serviceAccountUsername").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_ServiceAccountPassword")}>
						<SecretValueInput
							{...formHandler.for("serviceAccountPassword").attachToSecretValueInput()}
							containsInitialValue={Boolean(gatewayId)}
							isDisabled={isReadOnly}
						/>
						<ValidationError {
							...formHandler.for("serviceAccountPassword")
								.for("value")
								.attachToValidationError()}
						/>
					</FormItem>

					<FormItem label={localize("_Version")}>
						<Select
							{...formHandler.for("versionId").attachToSelect()}
							options={versions.map((x) => ({ value: x.id, text: x.name}))}
							isDisabled={isReadOnly}
							menuTitle={localize("_Version")}
						/>
						<ValidationError {...formHandler.for("versionId").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_LaunchExperience")}>
						<Checkbox
							{...formHandler.for("isNewLaunchExperienceEnabled").attachToCheckbox()}
							text={localize("_EnableNewLaunchExperience")}
							isDisabled={isReadOnly}
						/>
						<ValidationError {...formHandler.for("isNewLaunchExperienceEnabled").attachToValidationError()} />
					</FormItem>
				</Form>
			</Section>

			{ isReadOnly && (
				<ConnectionsSection
					connections={connections}
					failedToLoadConnections={failedToLoadConnections}
					selection={selectedConnections}
					onSelectionChange={setSelectedConnections}
				/>
			)}

			{ connectionIdsToBeDeleted && (
				<ConfirmationDialog
					title={localize("_DeleteConnectionConfirmationTitle")}
					confirmText={localize("_Delete")}
					onConfirmation={() => deleteConnectionsHandler(connectionIdsToBeDeleted)}
					onCancellation={() => setConnectionIdsToBeDeleted(undefined)}
					trackingId="delete-crdp-connection-confirmation-dialog"
				>
					{ localize("_DeleteConnectionConfirmationText")}
				</ConfirmationDialog>
			)}
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
GatewayConfiguration.displayName = "GatewayConfiguration";

export type GatewayConfigurationParams = RouteParametersFromProps<
	GatewayConfigurationProps,
	"gatewayId"
>;

export const GatewayConfigurationForRouter: FunctionComponent<Pick<GatewayConfigurationProps, "isReadOnly">> = connectToRouter(
	GatewayConfiguration,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback,
		"gatewayId": {
			isRequired: false,
			tryParse: tryParseInt
		}
	}
);

type GatewayActionContext = {
	gatewayId: number | undefined;
	selectedConnectionIds: string[];
	isEditing: boolean;
	isMobile: boolean;
};

type GatewayAction = ToolbarAction<GatewayActionContext>;

type ActionHandlers = {
	deselectHandler(): void;
	navigateToEdit(gatewayId: number): void;
	cancelHandler(gatewayId: number | undefined): void;
	createConnectionHandler(gatewayId: number): void;
	viewConnectionHandler(gatewayId: number, connectionId: string): void;
	editConnectionHandler(gatewayId: number, connectionId: string): void;
	saveHandler(): void;
	deleteConnectionsHandler(ids: string[]): void;
};

function listContextualActions(
	handlers: ActionHandlers
): readonly GatewayAction[] {
	const {
		deleteConnectionsHandler,
		viewConnectionHandler,
		editConnectionHandler
	} = handlers;

	const manageConnectionGroup: ToolbarActionGroup = {
		kind: "regular",
		name: "manageConnection",
		title: localize("_ManageConnection"),
		icon: "wrench"
	};

	return [
		{
			kind: ActionKind.Regular,
			name: "openConnection",
			title: localize("_Open"),
			group: manageConnectionGroup,
			canExecute: (c) => !c.isEditing
				&& Boolean(c.gatewayId)
				&& c.selectedConnectionIds.length === 1,
			execute: (c) => viewConnectionHandler(
				ensureNotNil(c.gatewayId),
				encodeURIComponent(c.selectedConnectionIds[0])
			)
		},
		{
			kind: ActionKind.Regular,
			name: "editConnection",
			title: localize("_Edit"),
			group: manageConnectionGroup,
			canExecute: (c) => !c.isEditing
				&& Boolean(c.gatewayId)
				&& c.selectedConnectionIds.length === 1,
			execute: (c) => {
				editConnectionHandler(
					ensureNotNil(c.gatewayId),
					encodeURIComponent(c.selectedConnectionIds[0])
				);
			}
		},
		{
			kind: ActionKind.Regular,
			name: "deleteConnection",
			title: localize("_Delete"),
			group: manageConnectionGroup,
			canExecute: (c) => !c.isEditing
				&& Boolean(c.gatewayId)
				&& c.selectedConnectionIds.length > 0,
			execute: (c) => deleteConnectionsHandler(c.selectedConnectionIds)
		}
	];
}

function listDefaultActions(
	handlers: ActionHandlers
): readonly GatewayAction[] {
	const {
		saveHandler,
		cancelHandler,
		navigateToEdit,
		createConnectionHandler
	} = handlers;

	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			icon: "floppy-disk",
			canExecute: (c) => c.isEditing,
			execute: () => saveHandler(),
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			icon: "xmark",
			canExecute: (c) => c.isEditing,
			execute: (c) => cancelHandler(c.gatewayId)
		},
		{
			kind: ActionKind.Regular,
			name: "editGateway",
			title: localize("_EditGateway"),
			icon: "pen",
			canExecute: (c) => !c.isEditing,
			execute: (c) => navigateToEdit(ensureNotNil(c.gatewayId))
		},
		{
			kind: ActionKind.Regular,
			name: "newConnection",
			title: localize("_NewConnection"),
			icon: "plus",
			canExecute: (c) => !c.isEditing && Boolean(c.gatewayId),
			execute: (c) => createConnectionHandler(ensureNotNil(c.gatewayId))
		}
	];
}

function getSelectionActions(
	handlers: ActionHandlers
): readonly GatewayAction[] {
	const {
		deselectHandler
	} = handlers;

	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selectedConnectionIds),
			icon: "circle-xmark",
			canExecute: (c) => c.selectedConnectionIds.length > 0,
			execute: () => deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selectedConnectionIds.length > 0
		}
	];
}

function convertToSaveModel(
	model: GatewayModel | null,
	versions: GatewayVersion[]
): GatewaySaveModel {
	if (!model) {
		return {
			id: undefined,
			name: "",
			gatewayUrl: "",
			serviceAccountUsername: "",
			serviceAccountPassword: {
				containsNewValue: true,
				value: ""
			},
			versionId: versions[0]?.id,
			isNewLaunchExperienceEnabled: false
		};
	}

	const {
		id,
		name,
		gatewayUrl,
		serviceAccountUsername,
		versionId,
		isNewLaunchExperienceEnabled
	} = model;

	return {
		id,
		name,
		gatewayUrl,
		serviceAccountUsername,
		serviceAccountPassword: {
			containsNewValue: false,
			value: ""
		},
		versionId,
		isNewLaunchExperienceEnabled
	};
}

const defaultViewModel: GatewayViewModel = {
	model: null,
	connections: [],
	failedToLoadConnections: false,
	isReadOnly: true,
	versions: []
};
